import { useState, useEffect } from "react";
import axios from "axios";
import { GetBillingData } from "../../../services/billingService";
import { triggerBillingRefresh } from "../../../utils/billingRefreshEvent";
import { triggerUtilityRefresh } from "../../../utils/utilityRefreshEvent";
import { resolveImageUrl } from "../../../utils";

declare const API_BASE_URL: string;

interface UserADData {
  Email: string;
  FullName: string;
  OfficeLocation: string;
  Title: string;
  PhoneNumber: string | null;
  AccountValue: string;
}

interface BillingData {
  BillingTypeId: number;
  BillingTypeName: string;
  Billing: number;
  Paid: number;
  Outstanding: number;
}

interface BillingResponse {
  billingDatas: BillingData[];
  TotalBilling: number;
  TotalPaid: number;
  TotalOutstanding: number;
  DueDate: string;
  LastUpdate: string;
  Code: number;
  Message: string;
}

const BILLING_FALLBACK: BillingResponse = {
  billingDatas: [],
  TotalBilling: 0,
  TotalPaid: 0,
  TotalOutstanding: 0,
  DueDate: "",
  LastUpdate: "",
  Code: -1,
  Message: "",
};

interface WelcomeSectionProps {
  selectedTenant?: number | null;
}

export default function WelcomeSection({ selectedTenant }: WelcomeSectionProps) {
  const [userData, setUserData] = useState<UserADData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billingData, setBillingData] = useState<BillingResponse | null>(null);
  const [currentSlide] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userEmail =
          localStorage.getItem("user_email") ||
          sessionStorage.getItem("user_email");
        const token =
          localStorage.getItem("access_token") ||
          sessionStorage.getItem("access_token");

        if (!userEmail || !token) {
          setLoading(false);
          return;
        }

        const url = `${API_BASE_URL}/user/v1/getuserad?email=${encodeURIComponent(userEmail)}`;
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        if (response.data?.Code === 0 && response.data.userADDatas?.length) {
          setUserData(response.data.userADDatas[0]);
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        let data = await GetBillingData(false);
        const isEmpty =
          !data ||
          data.Code !== 0 ||
          (data.TotalBilling === 0 &&
            (!data.billingDatas || data.billingDatas.length === 0));

        if (isEmpty) {
          data = await GetBillingData(true);
        }

        if (data) {
          setBillingData(
            data.Code !== 0
              ? { ...BILLING_FALLBACK, LastUpdate: data.LastUpdate }
              : data
          );
        }
      } catch (err) {
        console.log("Failed to load billing data", err);
        setBillingData(BILLING_FALLBACK);
      }
    };

    fetchBillingData();
  }, []);

  // ✅ useEffect subscribeTenantChange DIHAPUS dari sini
  // Supaya billing tidak terpanggil 2x saat tenant change
  // WelcomeSection akan update via prop selectedTenant jika dibutuhkan

  const handleRefresh = async () => {
    if (selectedTenant !== null && selectedTenant !== undefined) {
      localStorage.setItem("tenant_id", String(selectedTenant));
      sessionStorage.setItem("tenant_id", String(selectedTenant));
    }

    setRefreshing(true);
    try {
      // ✅ isreload=true hanya saat user klik tombol refresh manual
      const data = await GetBillingData(true);
      if (data) {
        setBillingData(
          data.Code !== 0
            ? { ...BILLING_FALLBACK, LastUpdate: data.LastUpdate }
            : data
        );
        triggerBillingRefresh();
        triggerUtilityRefresh();
      }
    } catch (err) {
      console.log("Failed to refresh billing data", err);
    } finally {
      setRefreshing(false);
    }
  };

  // ✅ Tambah useEffect ini — WelcomeSection update billing saat selectedTenant prop berubah
  useEffect(() => {
    if (selectedTenant === null || selectedTenant === undefined) return;

    const fetchBillingOnTenantChange = async () => {
      try {
        console.log("[WELCOME] selectedTenant prop changed:", selectedTenant);
        const data = await GetBillingData(false);
        if (data) {
          setBillingData(
            data.Code !== 0
              ? { ...BILLING_FALLBACK, LastUpdate: data.LastUpdate }
              : data
          );
        }
      } catch (err) {
        console.log("[WELCOME] Failed to fetch billing on tenant change", err);
        setBillingData(BILLING_FALLBACK);
      }
    };

    fetchBillingOnTenantChange();
  }, [selectedTenant]);

  const getWelcomeMessage = () => {
    if (loading) return "Loading...";
    if (error || !userData) return "Welcome";
    const displayName = userData.FullName;
    const role = userData.Title;
    let message = "Welcome";
    if (displayName) message += `, ${displayName}`;
    if (role) message += ` from ${role}`;
    return message;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("id-ID").format(Math.trunc(value));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
  };

  const slides = [
    {
      title: "Current Billing",
      amount: billingData?.TotalBilling || 0,
      dueDate: billingData?.DueDate,
      showWarning: true,
    },
  ];

  return (
    <div className="h-full flex flex-col w-full flex-1">
      <div className="relative rounded-2xl bg-blue-600 text-white p-4 sm:p-5 overflow-hidden flex flex-col h-full flex-1">

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="absolute top-4 right-4 sm:top-5 sm:right-5 z-20 text-white hover:text-gray-200 transition-colors disabled:opacity-50"
          title="Refresh billing data"
        >
          <svg
            className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>

        <div className="hidden md:block absolute right-0 bottom-0 w-44 h-48 pointer-events-none">
          <img
            src={resolveImageUrl("/images/icon (1).png")}
            alt="avatar"
            className="w-full h-full object-contain object-right-bottom translate-x-[12px] translate-y-[6px]"
          />
        </div>

        <div className="flex flex-col flex-1">
          <p className="text-sm sm:text-base font-medium mb-3 pr-8 truncate">
            {getWelcomeMessage()}
          </p>

          <div className="bg-white text-gray-900 rounded-xl p-4 sm:p-5 shadow-lg dark:bg-gray-800 relative z-10 overflow-hidden flex-1 flex flex-col md:max-w-[65%]">
            <div className="relative overflow-hidden flex-1 flex items-center">
              <div
                className="flex transition-transform duration-500 ease-in-out w-full"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {slides.map((slide, index) => (
                  <div key={index} className="w-full flex-shrink-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-gray-500 text-xs font-medium dark:text-gray-400">
                        {slide.title}
                      </h3>
                      <button className="text-gray-400 hover:text-gray-600 transition-colors">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <circle cx="10" cy="4" r="1.5" />
                          <circle cx="10" cy="10" r="1.5" />
                          <circle cx="10" cy="16" r="1.5" />
                        </svg>
                      </button>
                    </div>

                    <div className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 dark:text-white truncate">
                      {formatCurrency(slide.amount)}
                    </div>

                    {slide.dueDate ? (
                      <div className="flex items-center gap-1.5 text-xs mb-4">
                        <svg className="w-4 h-4 text-orange-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-orange-500 font-medium">
                          Due date {slide.dueDate ? formatDate(slide.dueDate) : "-"}
                        </span>
                      </div>
                    ) : (
                      <div className="mb-4 h-5" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}